import React from 'react';
import styled from 'styled-components';
import { blueLightCardImages } from '../Theming/images';
import { ANALYTICS_PAGE_NAMES, useIsIPad } from '../Common/Analytics/utils';
import { usePageViewTracking } from '../Common/Hooks';
import Header from './components/Header';
import { BoldText } from './BlueLightCardPluginActivationAlwaysAllow';
import ActivationStep from './components/ActivationStep';

const Container = styled.div<{ isIPad?: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100dvh;
  max-height: 100dvh;
  max-width: ${(props) => (props.isIPad ? '100%' : '600px')};
  margin: 0 auto;
  overflow: hidden;
`;

const InfoContainer = styled.div<{ isIPad?: boolean }>`
  padding-top: 35px;
  padding-inline: 20px;
  overflow-y: auto;
  flex: 1;

  ${({ isIPad }) =>
    isIPad &&
    `
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: left;
    margin-top: 0px;
  `}
`;

const InfoTitle = styled.h3<{ isIPad?: boolean }>`
  font-family: 'Museo Sans', sans-serif;
  font-weight: 600;
  font-size: ${({ isIPad }) => (isIPad ? '32px' : '24px')};
  line-height: ${({ isIPad }) => (isIPad ? '40px' : '32px')};
  letter-spacing: 0%;
  vertical-align: middle;
  margin: 20px 0 25px 0;
  text-align: center;
`;

const ActivationStepsContainer = styled.div<{
  isIPad?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 500px;
  margin: 50px auto;
  gap: 0;
`;

const BoldSpan = styled.span`
  font-weight: 700;
`;

const InfoContentWrapper = styled.div<{ isIPad?: boolean }>`
  ${({ isIPad }) =>
    isIPad &&
    `
    text-align: left;
  `}
`;

const BlueLightCardPluginActivationPartialPermissions = () => {
  const isIPad = useIsIPad();
  const { partially_installed_page } = ANALYTICS_PAGE_NAMES;
  usePageViewTracking(partially_installed_page, 'BlueLightCard');

  const steps = [
    {
      text: 'Go to Settings',
      image: blueLightCardImages.safari_setting_partial_1.default,
    },
    {
      text: (
        <>
          Search for <BoldSpan>Safari</BoldSpan>
        </>
      ),
      image: blueLightCardImages.safari_setting_partial_2.default,
    },
    {
      text: (
        <>
          Scroll down to <BoldSpan>Extensions</BoldSpan>
        </>
      ),
      image: blueLightCardImages.safari_setting_partial_3.default,
    },
    {
      text: (
        <>
          Select <BoldSpan>Blue Light Card Deal Finder</BoldSpan>
        </>
      ),
      image: blueLightCardImages.safari_setting_partial_4.default,
    },
    {
      text: (
        <>
          Toggle on <BoldSpan>Allow Extension</BoldSpan>
        </>
      ),
      image: blueLightCardImages.safari_setting_partial_5.default,
    },
    {
      text: (
        <>
          Scroll down and set to <BoldSpan>Allow</BoldSpan> on{' '}
          <BoldSpan>Other Websites</BoldSpan>
        </>
      ),
      image: blueLightCardImages.safari_setting_partial_6.default,
    },
  ];

  return (
    <Container isIPad={isIPad}>
      <Header
        height={isIPad ? 200 : 160}
        starTopPosition={isIPad ? '100%' : '85%'}
        heading={
          <>
            <BoldText>Blue Light Card</BoldText>{' '}
            <BoldText>Deal Finder is only</BoldText>
            <BoldText>partially installed.</BoldText>
          </>
        }
      />

      <InfoContainer isIPad={isIPad}>
        <InfoContentWrapper isIPad={isIPad}>
          <InfoTitle isIPad={isIPad}>
            Here&apos;s what you need to do:
          </InfoTitle>
          <ActivationStepsContainer isIPad={isIPad}>
            {steps.map((step, index) => (
              <ActivationStep
                key={index}
                stepNumber={index + 1}
                text={step.text}
                imageSrc={step.image}
                showArrow={index < steps.length - 1}
              />
            ))}
          </ActivationStepsContainer>
        </InfoContentWrapper>
      </InfoContainer>
    </Container>
  );
};

export default BlueLightCardPluginActivationPartialPermissions;
